import { SectionSkeleton } from "@app/screens/Frameworks/framework/components/Section";
import { FlexCol } from "@design-system/Layout/Flex";
import { type QuestionFilter } from "@generated/client/graphql";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useQueryParam } from "use-query-params";
import { questionnaireLocaleAtom, questionnaireParamsArgs } from "../../../ctx";
import { useQuestions } from "../../../data";
import {
    filterQuestionsByDependency,
    isQuestionGenerating,
} from "../../../services";
import { useQuestionnaireGroups } from "../data";
import { Question } from "./Question";
import { QuestionsWrapper } from "./QuestionsWrapper";

export const Questions = ({ filter }: { filter?: QuestionFilter }) => {
    const currentLocale = useAtomValue(questionnaireLocaleAtom);
    const { groups, isPending: isGroupsPending } = useQuestionnaireGroups(
        filter?.questionnaireId ?? undefined,
        currentLocale,
    );
    const [activeGroupIdParams] = useQueryParam(
        ...questionnaireParamsArgs.group,
    );

    const [pollingInterval, setPollingInterval] = useState<number | undefined>(
        1000,
    );

    const { questions, isPending: isQuestionsPending } = useQuestions({
        searchString: filter?.searchString ?? undefined,
        questionnaireId: filter?.questionnaireId ?? undefined,
        groupId: activeGroupIdParams ?? undefined,
        ownerIds: filter?.ownerIds ?? [],
        statuses: filter?.statuses ?? [],
        aiAnswerStatuses: filter?.aiAnswerStatuses ?? [],
        types: filter?.types ?? [],
        opts: {
            pollingInterval,
        },
    });

    useEffect(() => {
        if (questions) {
            const anyGenerating =
                questions.filter((q) => isQuestionGenerating(q)).length > 0;
            setPollingInterval(anyGenerating ? 1000 : undefined);
        }
    }, [questions]);

    const filteredQuestions = useMemo(() => {
        return filterQuestionsByDependency(questions);
    }, [questions, activeGroupIdParams]);

    const activeGroup = useMemo(() => {
        if (!groups?.length) return null;
        return groups.find((group) => group.id === activeGroupIdParams);
    }, [groups, activeGroupIdParams]);

    if (isGroupsPending || isQuestionsPending) {
        return (
            <FlexCol gap="4" w="full" p="4">
                {Array.from({ length: 2 }).map((_, i) => (
                    <SectionSkeleton key={i} />
                ))}
            </FlexCol>
        );
    }

    if (activeGroup) {
        return (
            <QuestionsWrapper>
                <QuestionsWrapper.GroupTitle id={activeGroupIdParams ?? ""}>
                    {activeGroup.name}
                </QuestionsWrapper.GroupTitle>
                <QuestionsWrapper.Group>
                    {filteredQuestions?.length > 0 ? (
                        filteredQuestions?.map((question) => (
                            <Question
                                question={question}
                                key={question.id}
                                locale={currentLocale}
                                filter={{ groupId: activeGroupIdParams }}
                            />
                        ))
                    ) : (
                        <QuestionsWrapper.EmptyGroup />
                    )}
                </QuestionsWrapper.Group>
            </QuestionsWrapper>
        );
    } else {
        return (
            <QuestionsWrapper>
                <QuestionsWrapper.Group>
                    {filteredQuestions?.map((question) => (
                        <Question
                            key={question.id}
                            question={question}
                            locale={currentLocale}
                            filter={{ groupId: activeGroupIdParams }}
                        />
                    ))}
                </QuestionsWrapper.Group>
            </QuestionsWrapper>
        );
    }
};
